import { ShaderProgram } from '../renderer/ShaderProgram';
import { InstancedMesh } from '../renderer/InstancedMesh';
import type { Camera } from '../renderer/Camera';
import type { Renderable } from '../renderer/RenderPipeline';
import type { StellarRenderConfig } from '../config/StellarRenderConfig';
import type { ClusterContent } from '../network/ContentLoader';
import { axialToWorld } from '../math/HexMath';
import { createIcoSphere } from '../renderer/GeometryFactory';

import vertSrc from '../shaders/planet.vert.glsl?raw';
import prefixSrc from '../shaders/glsl-prefix.glsl?raw';
import noiseSrc from '../shaders/noise3d.glsl?raw';
import fragSrc from '../shaders/planet.frag.glsl?raw';

const FLOATS_PER_INSTANCE = 5;

const TYPE_INDEX: Record<string, number> = {
  terran: 0, desert: 1, ice: 2, lava: 3,
};

export class PlanetRenderer implements Renderable {
  private _shader: ShaderProgram;
  private _mesh: InstancedMesh;
  private _config: StellarRenderConfig;
  private _hexRadius: number;
  private _planetCount = 0;
  private _startTime = performance.now();
  private _instanceBuffer: Float32Array;

  constructor(
    gl: WebGL2RenderingContext,
    config: StellarRenderConfig,
    hexRadius: number,
    maxInstances: number,
  ) {
    this._config = config;
    this._hexRadius = hexRadius;
    this._instanceBuffer = new Float32Array(maxInstances * FLOATS_PER_INSTANCE);

    const combinedFrag = this._buildFragSource();
    this._shader = new ShaderProgram(gl, vertSrc, combinedFrag);

    const sphere = createIcoSphere(4);
    const geoData = this._interleaveSphere(sphere.positions, sphere.normals);

    this._mesh = new InstancedMesh(
      gl, this._shader, geoData,
      [
        { name: 'a_position', size: 3, divisor: 0 },
        { name: 'a_normal', size: 3, divisor: 0 },
      ],
      [
        { name: 'a_worldPos', size: 3, divisor: 1 },
        { name: 'a_planetType', size: 1, divisor: 1 },
        { name: 'a_size', size: 1, divisor: 1 },
      ],
      sphere.indices,
    );

    this._uploadUniforms(gl);
  }

  updateFromContent(contents: ClusterContent[]): void {
    let count = 0;
    const max = this._instanceBuffer.length / FLOATS_PER_INSTANCE;
    for (let c = 0; c < contents.length; c++) {
      const planets = contents[c].planets;
      for (let i = 0; i < planets.length; i++) {
        if (count >= max) break;
        const p = planets[i];
        if (p.axial_q == null || p.axial_r == null) continue;
        const [wx, wz] = axialToWorld(p.axial_q, p.axial_r, this._hexRadius);
        const idx = count * FLOATS_PER_INSTANCE;
        const size = (p.scale ?? 1.0) * this._config.planetBaseSize;
        this._instanceBuffer[idx] = wx;
        this._instanceBuffer[idx + 1] = size;
        this._instanceBuffer[idx + 2] = wz;
        this._instanceBuffer[idx + 3] = TYPE_INDEX[p.planet_type ?? 'terran'] ?? 0;
        this._instanceBuffer[idx + 4] = size;
        count++;
      }
    }
    this._planetCount = count;
    this._mesh.updateInstances(
      this._instanceBuffer.subarray(0, count * FLOATS_PER_INSTANCE), count,
    );
  }

  get planetCount(): number { return this._planetCount; }

  render(gl: WebGL2RenderingContext, camera: Camera): void {
    if (this._planetCount === 0) return;

    gl.enable(gl.DEPTH_TEST);
    gl.depthMask(true);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    this._shader.use();
    this._shader.setMat4('u_projection', camera.projection.data);
    this._shader.setMat4('u_view', camera.view.data);
    this._shader.setFloat('u_time', (performance.now() - this._startTime) / 1000.0);
    this._shader.setVec3('u_cameraPos',
      camera.position.x, camera.position.y, camera.position.z);

    this._mesh.draw();
  }

  dispose(): void {
    this._shader.dispose();
    this._mesh.dispose();
  }

  private _buildFragSource(): string {
    return prefixSrc + noiseSrc + '\n' + fragSrc;
  }

  private _interleaveSphere(pos: Float32Array, norm: Float32Array): Float32Array {
    const count = pos.length / 3;
    const out = new Float32Array(count * 6);
    for (let i = 0; i < count; i++) {
      const i3 = i * 3, i6 = i * 6;
      out[i6] = pos[i3]; out[i6 + 1] = pos[i3 + 1]; out[i6 + 2] = pos[i3 + 2];
      out[i6 + 3] = norm[i3]; out[i6 + 4] = norm[i3 + 1]; out[i6 + 5] = norm[i3 + 2];
    }
    return out;
  }

  private _uploadUniforms(gl: WebGL2RenderingContext): void {
    this._shader.use();
    const types = ['terran', 'desert', 'ice', 'lava'];
    const prog = this._shader.program;

    for (let i = 0; i < 4; i++) {
      const info = this._config.planetTypes[types[i]];
      this._setVec3(gl, prog, `u_deepColors[${i}]`, info.colorDeep);
      this._setVec3(gl, prog, `u_shoreColors[${i}]`, info.colorShore);
      this._setVec3(gl, prog, `u_coastColors[${i}]`, info.colorCoast);
      this._setVec3(gl, prog, `u_landColors[${i}]`, info.colorLand);
      this._setVec3(gl, prog, `u_peakColors[${i}]`, info.colorPeak);
      this._setVec3(gl, prog, `u_atmosColors[${i}]`, info.colorAtmos);
      this._setVec3(gl, prog, `u_cloudColors[${i}]`, info.colorCloud);
      this._setF(gl, prog, `u_waterLevels[${i}]`, info.waterLevel);
      this._setF(gl, prog, `u_peakLevels[${i}]`, info.peakLevel);
      this._setF(gl, prog, `u_noiseScales[${i}]`, info.noiseScale);
      this._setF(gl, prog, `u_cloudDensities[${i}]`, info.cloudDensity);
      this._setF(gl, prog, `u_cloudSpeeds[${i}]`, info.cloudSpeed);
      this._setF(gl, prog, `u_hasClouds[${i}]`, info.hasClouds ? 1.0 : 0.0);
      this._setF(gl, prog, `u_hasAtmos[${i}]`, info.hasAtmosphere ? 1.0 : 0.0);
    }
  }

  private _setVec3(
    gl: WebGL2RenderingContext, prog: WebGLProgram,
    name: string, c: readonly [number, number, number],
  ): void {
    const loc = gl.getUniformLocation(prog, name);
    if (loc) gl.uniform3f(loc, c[0], c[1], c[2]);
  }

  private _setF(
    gl: WebGL2RenderingContext, prog: WebGLProgram,
    name: string, v: number,
  ): void {
    const loc = gl.getUniformLocation(prog, name);
    if (loc) gl.uniform1f(loc, v);
  }
}
